import { SeverityBadge } from "./SeverityBadge";
import { ConfidenceBadge } from "./ConfidenceBadge";
import { ListChecks, ArrowRight } from "lucide-react";

/**
 * Top 5 Priorities — calm, scannable section at the top of every report.
 *
 * Renders ONLY when stored data exists. No fallbacks, no AI calls. Reports
 * generated before v2.9 won't have `top_5_priorities` and this returns null.
 */
export default function ReportTopPriorities({ priorities }) {
    if (!Array.isArray(priorities) || priorities.length === 0) return null;
    return (
        <section className="space-y-5" data-testid="report-top-priorities">
            <div>
                <div className="bl-eyebrow">
                    <ListChecks className="w-3.5 h-3.5" /> Top priorities
                </div>
                <h2 className="mt-3 font-display font-semibold text-2xl md:text-3xl tracking-tight text-bl">
                    What matters most — in order.
                </h2>
                <p className="mt-3 text-bl-muted leading-relaxed max-w-2xl">
                    Five items to focus on first. Sequenced by negotiation
                    relevance, urgency, and exposure.
                </p>
            </div>
            <ol className="space-y-3 list-none">
                {priorities.map((p, i) => (
                    <li
                        key={p.issue_id || i}
                        className="bl-card p-5 md:p-6 flex gap-4"
                        data-testid={`report-top-priority-${i + 1}`}
                    >
                        <div className="font-mono-bl text-bl-primary text-xl tnum w-8 flex-shrink-0">
                            0{i + 1}
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                                {p.severity && <SeverityBadge severity={p.severity} />}
                                {p.category && (
                                    <span className="text-[11px] tracking-[0.18em] uppercase text-bl-muted font-medium">
                                        {p.category}
                                    </span>
                                )}
                                {p.confidence && (
                                    <ConfidenceBadge confidence={p.confidence} />
                                )}
                            </div>
                            <h3 className="font-display font-semibold text-bl leading-snug">
                                {p.title}
                            </h3>
                            {p.why_it_matters && (
                                <p className="mt-2 text-bl-muted text-sm leading-relaxed">
                                    {p.why_it_matters}
                                </p>
                            )}
                            {p.recommended_next_step && (
                                <div className="mt-3 flex items-start gap-2 text-sm text-bl">
                                    <ArrowRight className="w-3.5 h-3.5 mt-0.5 text-bl-primary flex-shrink-0" />
                                    <span>{p.recommended_next_step}</span>
                                </div>
                            )}
                        </div>
                    </li>
                ))}
            </ol>
        </section>
    );
}
